import { Box, Table } from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useMemo } from "react";
import { t } from "i18next";
import {
  CraAttribute,
  CraAttributes,
  CraAttributeName,
  CraAttributeType,
  DistanceUnit,
} from "@keita/lanepatrol-sdk";
import { getAttributeValue } from "../../../../../coding/utils/attribute-value";
import { CraAttributeUtils } from "../../../../../coding/utils/attribute-utils";
import { useStores } from "../../../../../shared/stores/RootStore";
import { formatDistanceByUnit } from "../../../../../shared/utils/format-utils";

const i18prefix = "map:dashboard.TableDistancePerAttributeValue";

type RowData = {
  treatment: string;
  code: string | number;
  option: string;
  distance: number;
  distanceFormatted: string;
  percentage: number;
};

const TableDistancePerAttributeValueBase = () => {
  const { filterStore, projectStore } = useStores();
  const codingEntries = filterStore.originalOrForecastedAllNodeCodingsByWayId;

  const distanceUnit = projectStore.distanceUnit;

  const data = useMemo(() => {
    const counter = new Map<
      CraAttribute,
      Map<string | number | null, number>
    >();

    for (const node of filterStore.filteredNodes) {
      const codingEntry = codingEntries.get(node.osaWayId)?.get(node.id);

      if (codingEntry) {
        for (const attributeDefinition of CraAttributes.all) {
          if (attributeDefinition.type === CraAttributeType.TextArea) continue;

          const attributeValue = getAttributeValue(
            attributeDefinition.name,
            codingEntry.attributes,
          );

          const subCounter =
            counter.get(attributeDefinition) ??
            new Map<string | number | null, number>();

          const totalDistance = subCounter.get(attributeValue) ?? 0;
          subCounter.set(attributeValue, totalDistance + node.distanceToNext);

          counter.set(attributeDefinition, subCounter);
        }
      }
    }

    return [...counter.entries()];
    // eslint-disable-next-line
  }, [filterStore.filteredNodes, codingEntries]);

  const rowGroups = useMemo(() => {
    const out: RowData[][] = [];

    for (const [attributeDefinition, subCounter] of data) {
      let showCode = true;
      let showOnlySelectedOptions = false;

      if (
        attributeDefinition.name === CraAttributeName.RoadOperatingSpeed ||
        attributeDefinition.name === CraAttributeName.RoadAADT
      ) {
        showOnlySelectedOptions = true;
        showCode = false;
      }

      const treatmentLabel = CraAttributeUtils.getLabel(attributeDefinition.name);

      type SubRow = { label: string; distance: number };
      const subRows: SubRow[] = [];

      if (attributeDefinition.type === CraAttributeType.Select) {
        for (const option of attributeDefinition.options) {
          const optionDistance = subCounter.get(option.value) ?? 0;
          if (!(showOnlySelectedOptions && optionDistance === 0)) {
            subRows.push({
              label: CraAttributeUtils.getOptionLabel(option.value),
              distance: optionDistance,
            });
          }
        }
      } else if (attributeDefinition.name === CraAttributeName.RoadSpeedLimit) {
        showCode = false;

        [...subCounter]
          .sort((a, b) => (a[0] as number) - (b[0] as number))
          .forEach(([speed, distance]) => {
            if (speed != null) {
              subRows.push({
                label: `${speed} ${
                  distanceUnit === DistanceUnit.Imperial ? "mph" : "km/h"
                }`,
                distance,
              });
            }
          });
      }

      out.push(
        subRows.map((sr, idx) => {
          const percentage =
            filterStore.filteredCraDistance > 0
              ? (sr.distance / filterStore.filteredCraDistance) * 100
              : 0;

          return {
            treatment: treatmentLabel,
            code: showCode ? idx + 1 : "NA",
            option: sr.label,
            distance: sr.distance,
            distanceFormatted: formatDistanceByUnit(sr.distance, distanceUnit),
            percentage: Number(percentage.toFixed(1)),
          };
        }),
      );
    }

    return out;
  }, [data, distanceUnit, filterStore.filteredCraDistance]);

  return (
    <Box width="100%" py={2}>
      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader pl={4}>
              {t(`${i18prefix}.header.treatment`)}
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">
              {t(`${i18prefix}.header.code`)}
            </Table.ColumnHeader>
            <Table.ColumnHeader>
              {t(`${i18prefix}.header.option`)}
            </Table.ColumnHeader>
            <Table.ColumnHeader textAlign="right">
              {t(`${i18prefix}.header.distance`, { unit: distanceUnit })}
            </Table.ColumnHeader>
            <Table.ColumnHeader pr={4} textAlign="right">
              {t(`${i18prefix}.header.percentage`)}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {rowGroups.flatMap((group, groupIdx) =>
            group.map((r, idx) => (
              <Table.Row key={`${groupIdx}-${idx}`}>
                {idx === 0 && (
                  <Table.Cell rowSpan={group.length} pl={4} w="180px">
                    {r.treatment}
                  </Table.Cell>
                )}
                <Table.Cell textAlign="right">{r.code}</Table.Cell>
                <Table.Cell>{r.option}</Table.Cell>
                <Table.Cell textAlign="right">{r.distanceFormatted}</Table.Cell>
                <Table.Cell pr={4} textAlign="right">
                  {r.percentage.toFixed(1)}
                </Table.Cell>
              </Table.Row>
            )),
          )}
        </Table.Body>
      </Table.Root>
    </Box>
  );
};

export const TableDistancePerAttributeValue = observer(
  TableDistancePerAttributeValueBase,
);
